import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormLabel,
  FormGroup,
  RadioGroup,
  Radio,
} from "@mui/material";

function AssessmentDialog({ open, template, responses, onChange, onCancel, onSubmit }) {
  const handleOptionToggle = (questionId, option) => {
    onChange(questionId, option.value, option.score);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{template?.name}</DialogTitle>
      <DialogContent>
        {template && (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {template.description}
            </Typography>
            {template.instructions && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {template.instructions}
              </Alert>
            )}
            {template.questions.map((question, index) => {
              const labelId = `assessment-question-${question.id}-label`;
              const isMulti = question.type === "checkbox";
              return (
                <FormControl
                  component="fieldset"
                  key={question.id}
                  sx={{ display: "block", mb: 3 }}
                >
                  <FormLabel
                    component="legend"
                    id={labelId}
                    sx={{ color: "text.primary", fontSize: "0.875rem", fontWeight: 500, mb: 1 }}
                  >
                    {index + 1}. {question.text}
                  </FormLabel>
                  {isMulti ? (
                    <FormGroup role="group" aria-labelledby={labelId}>
                      {question.options.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox
                              checked={responses[question.id]?.value === option.value}
                              onChange={() => handleOptionToggle(question.id, option)}
                              data-question-id={question.id}
                              data-option-value={option.value}
                            />
                          }
                          label={option.label}
                        />
                      ))}
                    </FormGroup>
                  ) : (
                    <RadioGroup
                      aria-labelledby={labelId}
                      value={
                        responses[question.id]?.value === undefined
                          ? ""
                          : String(responses[question.id].value)
                      }
                    >
                      {question.options.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={String(option.value)}
                          control={
                            <Radio
                              onChange={() => handleOptionToggle(question.id, option)}
                              data-question-id={question.id}
                              data-option-value={option.value}
                            />
                          }
                          label={option.label}
                        />
                      ))}
                    </RadioGroup>
                  )}
                </FormControl>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}
          aria-label="Cancel assessment"
          data-action="cancel-assessment"
        >
          Cancel
        </Button>
        <Button onClick={onSubmit} variant="contained" color="primary" disabled={!template}
          aria-label="Submit assessment"
          data-action="submit-assessment"
        >
          Submit Assessment
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AssessmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  template: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    instructions: PropTypes.string,
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        text: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
            score: PropTypes.number,
          })
        ).isRequired,
      })
    ),
  }),
  responses: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

AssessmentDialog.defaultProps = {
  template: null,
};

export default AssessmentDialog;
